// The four actor slots (CLAUDE.md: Game screens share one tile size; Call Sheet
// on mobile).
//
// **Four fixed slots, all four drawn from the first guess.** A slot whose actor
// has not been revealed holds a placeholder rather than empty space, so a player
// can see how many reveals are still to come. Every slot is in the layout from
// the start either way, so a reveal fills a space that was already there:
// nothing moves and nothing resizes as the game goes on.
//
// **This component is given the revealed actors and a count, never the puzzle.**
// That is deliberate and it is the security boundary: an unrevealed actor's name
// and photo path are not passed in, so they cannot be rendered, cannot be read
// out of the page source, and cannot be fetched early by accident. The rule is
// CLAUDE.md's — the real photo and name must never reach the browser before its
// reveal, and no blurred or low-resolution version of the photo either, because
// a blur of the real image is still the real image.
//
// **Photos are never cropped, letterboxed or distorted.** The game is about
// recognising a face, so the tile is 2:3 and the image covers it from the top —
// a head is at the top of a portrait, and that is the part to keep.
import { profileUrl, type Actor } from './types.ts';

// An actor's initials, for an image that fails to load. It is a fallback and
// never a normal state: the generator excludes anyone without a usable photo.
function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('');
}

// A drawing the site owns. It stands for an actor without being one: no name,
// no likeness, nothing fetched. It sits under every tile, revealed or not, which
// is what makes the reveal a cross-fade rather than a swap — the photo simply
// arrives on top of it.
//
// **A question mark, and nothing more figurative than that.** A silhouette was
// built and compared on a phone, and it loses for being a picture of a person:
// a head and shoulders in a tile that is about to hold a head and shoulders
// reads as a face that has not loaded yet, and the eye keeps going back to it.
// A question mark says what the slot is for and claims nothing about who is in
// it. A soft-focus version was tried earlier and dropped for a worse version of
// the same fault — see the rules.
function Placeholder() {
  return <span className="cs-ph cs-ph-question" aria-hidden="true">?</span>;
}

function Tile({ actor }: { actor: Actor | null }) {
  const url = actor ? profileUrl(actor.photo) : null;
  return (
    <figure className="cs-tile">
      <div className="cs-photo">
        <Placeholder />
        {actor && (url
          ? <img className="cs-photo-img" src={url} alt="" />
          : <span className="cs-initials" aria-hidden="true">{initials(actor.name)}</span>)}
      </div>
      {/* The name is the clue, so it is never truncated — and the caption row is
          a fixed two lines whether or not it holds one, so a reveal cannot
          change the height of the row. */}
      <figcaption className="cs-name">{actor?.name ?? ''}</figcaption>
    </figure>
  );
}

interface Props {
  /** In reveal order. Only the actors a player has earned. */
  revealed: Actor[];
  /** How many slots the game has — four. */
  slots: number;
}

export function ActorTiles({ revealed, slots }: Props) {
  return (
    <div className="cs-tiles">
      {Array.from({ length: slots }, (_, i) => {
        const actor = revealed[i] ?? null;
        return (
          <div className="cs-slot" key={i}>
            <Tile actor={actor} />
            {!actor && <span className="visually-hidden">Not revealed yet</span>}
          </div>
        );
      })}
    </div>
  );
}
